import { Checkbox, FormControlLabel, InputLabel, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import React, { ReactElement, ReactNode } from 'react';

const useStyles = makeStyles<Theme, { error: boolean }>((theme) => ({
  root: {
    position: 'relative',
  },
  inputLabel: {
    position: 'absolute',
    left: '12px',
    top: 0,
    // slight alteration to spec spacing to match visual spec result
    transform: 'translate(0, 24px) scale(1)',
  },
  container: {
    position: 'relative',
    marginTop: '8px',
    marginBottom: 10,
  },
  error: {
    color: theme.palette.error.main,
  },
  notchedOutline: (props) => ({
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: '1px',
    borderColor: props.error
      ? theme.palette.error.main
      : theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.23)'
      : '#ffffff',
  }),
  noMarginPadding: {
    margin: 0,
  },
  newOutline: {
    boxShadow:
      theme.palette.mode === 'light'
        ? '0px 0px 0px 1px rgba(0,0,0,0.3)'
        : '0px 0px 0px 1px rgba(250,250,250,0.3)',
    borderRadius: '4px',
    // padding: '8.5px 14px',
    position: 'relative',
    // zIndex: 123,
  },
  newLabel: (props) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#121c21' : '#ffffff',
    color: props?.error
      ? theme.palette.error.main
      : theme.palette.mode === 'dark'
      ? 'white'
      : 'rgba(0, 0, 0, 0.6)',
    fontFamily: 'Open Sans',
    fontWeight: '400',
    fontSize: '1rem',
    lineHeight: '1.4375em',
    display: 'block',
    verticalAlign: 'center',
    transformOrigin: 'top left',
    maxWidth: 'calc(133% - 32px)',
    position: 'absolute',
    left: '0',
    top: '0',
    transform: 'translate(14px, -9px) scale(0.75)',
    // zIndex: 1234,
    padding: '0 4px',
    maxHeight: '30px',
  }),
}));

/**
 * Create Material-UI outline border
 *
 * @description see ILabelledOutline interface for details
 *
 * @see https://stackoverflow.com/questions/58402130/how-can-i-set-an-static-outlined-div-similar-to-material-uis-outlined-textfield/58421725#58421725
 * @see https://stackoverflow.com/questions/54325908/change-outline-for-outlinedinput-with-react-material-ui
 * @see https://stackoverflow.com/questions/58559263/typescript-jsdoc-for-interface-properties
 */
export const LabelledOutline = (props: {
  /** Queue Identifier */
  id?: string;
  /** Label for outline */
  label: string;
  /** Element to be inserted into outline */
  children: ReactNode;
  /** className for styling */
  className?: string;
  /** Indicates error */
  error?: boolean;
  /** Error text to show */
  errorText?: string;
  /** Turn on select all/none */
  selectAllOnClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  selectAllIsAllChecked?: boolean;
  selectAllIsNoneChecked?: boolean;
  outlineClasses?: object | string | undefined;
  minHeight?: number;
  labelLeft?: number;
  customPadding?: string;
  backgroundColor?: string;
}): ReactElement => {
  const classes = useStyles({ error: Boolean(props.error) });
  const { customPadding } = props;
  const contentPadding = !customPadding ? '18.5px 14px' : customPadding;
  return (
    <div className={props?.className}>
      <div
        style={{ minHeight: props.minHeight ? `${props.minHeight}px` : 'auto' }}
        className={clsx(
          classes.container,
          classes.newOutline,
          classes.notchedOutline
        )}
      >
        <InputLabel
          sx={{
            left: props.labelLeft ? `${props.labelLeft}px` : '0',
            backgroundColor: (theme) =>
              props?.backgroundColor ?? theme.palette.background.default,
          }}
          className={classes.newLabel}
        >
          {props.label}
        </InputLabel>
        <div
          style={{
            minHeight: props.minHeight ? `${props.minHeight}px` : 'auto',
          }}
          aria-label="outline-body"
          className={clsx(
            classes.root,
            props.outlineClasses && props.outlineClasses
          )}
        >
          <div
            id={props.id}
            aria-label="outline-body-content"
            style={{ padding: contentPadding }}
          >
            {props.selectAllOnClick && (
              <>
                <FormControlLabel
                  className={classes.noMarginPadding}
                  control={
                    <Checkbox
                      color="primary"
                      onClick={props.selectAllOnClick}
                      checked={props.selectAllIsAllChecked}
                      indeterminate={
                        !props.selectAllIsAllChecked &&
                        !props.selectAllIsNoneChecked
                      }
                    />
                  }
                  label="Select All/None"
                />
                <hr />
              </>
            )}
            {props.children}
          </div>
        </div>
      </div>
    </div>
  );
};
